import type { ComponentPublicInstance } from 'vue'
import { ref } from 'vue'
import { Router } from '@/helpers/router.ts'
import { useLogger } from '@/composables/useLogger.ts'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { isAxiosError } from 'axios'
import { clone } from 'lodash-es'
import { useFormatter } from '@/composables/useFormatter.ts'

const visible = ref(false)
const onCloseCallback = ref<(() => void) | null>(null)
const logger = useLogger('error-service')

const error = ref<{
  error?: unknown
  component?: ComponentPublicInstance | null
  axios?: AxiosErrorBag
}>({})

let setupDone = false

export interface AxiosErrorBag {
  message: string
  name: string
  method: string
  url: string
  config: AxiosRequestConfig
  request: unknown
  response: AxiosResponse<unknown, unknown> | undefined
}

export function useErrorDialogService() {
  function setup() {
    if (!setupDone) {
      setupDone = true
      Router.beforeEach((_to, _from, next) => {
        clear()
        next()
      })
    }
  }

  if (import.meta.hot) {
    import.meta.hot.on('vite:beforeUpdate', () => {
      clear()
    })
  }

  function errorHandler(err: unknown, instance: ComponentPublicInstance | null): void {
    error.value = { ...error.value, error: err, component: instance }
    showError()
  }

  function setAxiosError(err: Required<AxiosError> | undefined): void {
    if (!err || !isAxiosError(err)) {
      error.value = { ...error.value, axios: undefined }
      return
    }

    const { request } = err
    const { response } = err
    const config = clone(err.config)
    if (config.headers.Authorization) {
      config.headers.Authorization = useFormatter().Truncate(config.headers.Authorization.toString(), { length: 40 })
    }
    const compressed: AxiosErrorBag = {
      message: err.message,
      name: err.name,
      method: config.method?.toUpperCase() ?? ``,
      url: `${config.baseURL}${config.url}`,
      config: config,
      request: request,
      response: response
    }
    error.value = { ...error.value, axios: compressed }
  }

  function clear(): void {
    error.value = {}
    visible.value = false
    logger.debug('Clear called')
  }

  function showError(options?: { onClose?: () => void }) {
    visible.value = false
    onCloseCallback.value = options?.onClose ?? null
    visible.value = true

    // visible.value = false
  }

  function close() {
    visible.value = false
    if (onCloseCallback.value) {
      onCloseCallback.value()
      onCloseCallback.value = null
    }
  }

  return {
    setup,
    errorHandler,
    clear,
    visible,
    error,
    showError,
    setAxiosError,
    close
  }
}
